// AI Recording Studio Pro — Projects Management Page
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { useRecordingStore } from "@/stores/recording-store";
import { RecordingStudioLayout } from "@/components/layout/RecordingStudioLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { motion } from "framer-motion";
import {
  FolderKanban,
  Plus,
  Search,
  Star,
  Clock,
  HardDrive,
  FileVideo,
  Trash2,
  Pencil,
  MoreVertical,
  Filter,
  SortAsc,
  Archive,
  Play,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_CONFIG = {
  draft: { label: "Brouillon", color: "bg-slate-500/10 text-slate-600 border-slate-300" },
  recording: { label: "En cours", color: "bg-amber-500/10 text-amber-600 border-amber-300" },
  editing: { label: "Édition", color: "bg-blue-500/10 text-blue-600 border-blue-300" },
  exporting: { label: "Export", color: "bg-purple-500/10 text-purple-600 border-purple-300" },
  completed: { label: "Terminé", color: "bg-emerald-500/10 text-emerald-600 border-emerald-300" },
  archived: { label: "Archivé", color: "bg-gray-500/10 text-gray-500 border-gray-300" },
};

const formatDuration = (s) => {
  if (!s) return "0m";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const formatSize = (b) => {
  if (!b) return "0 MB";
  if (b >= 1073741824) return `${(b / 1073741824).toFixed(1)} GB`;
  if (b >= 1048576) return `${(b / 1048576).toFixed(0)} MB`;
  return `${(b / 1024).toFixed(0)} KB`;
};

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export default function RecordingProjectsPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const {
    projects, fetchProjects, projectsLoading,
    createProject, updateProject, deleteProject,
  } = useRecordingStore();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "", theme: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (token) fetchProjects(token).catch(() => {});
  }, [token, fetchProjects]);

  // Filtered projects
  const filtered = projects.filter((p) => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (showFavoritesOnly && !p.is_favorite) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        p.name.toLowerCase().includes(q) ||
        (p.theme || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  const handleCreate = async () => {
    if (!formData.name.trim()) return toast.error("Nom requis");
    setSaving(true);
    try {
      await createProject(token, formData);
      toast.success("Projet créé");
      setShowNewDialog(false);
      setFormData({ name: "", description: "", theme: "" });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) return toast.error("Nom requis");
    setSaving(true);
    try {
      await updateProject(token, editProject.id, formData);
      toast.success("Projet modifié");
      setEditProject(null);
    } catch {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (project) => {
    if (!window.confirm(`Supprimer "${project.name}" ?`)) return;
    try {
      await deleteProject(token, project.id);
      toast.success("Projet supprimé");
    } catch {
      toast.error("Erreur");
    }
  };

  const handleToggleFavorite = async (project) => {
    try {
      await updateProject(token, project.id, { is_favorite: !project.is_favorite });
    } catch {
      toast.error("Erreur");
    }
  };

  const openEdit = (project) => {
    setFormData({
      name: project.name,
      description: project.description || "",
      theme: project.theme || "",
    });
    setEditProject(project);
  };

  return (
    <RecordingStudioLayout>
      <div className="p-6 md:p-8 space-y-6 max-w-[1200px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <FolderKanban className="h-6 w-6 text-primary" />
              Projets
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {projects.length} projet{projects.length !== 1 ? "s" : ""}
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => {
              setFormData({ name: "", description: "", theme: "" });
              setShowNewDialog(true);
            }}
            className="gap-1.5"
            style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)", border: "none" }}
          >
            <Plus className="h-4 w-4" />
            Nouveau projet
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 text-xs"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-[140px] text-xs">
              <Filter className="h-3 w-3 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">Tous les statuts</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <SelectItem key={key} value={key} className="text-xs">{cfg.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant={showFavoritesOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            className="gap-1 h-8 text-xs"
          >
            <Star className={`h-3 w-3 ${showFavoritesOnly ? "fill-current" : ""}`} />
            Favoris
          </Button>
        </div>

        {/* Projects Grid */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((project, i) => {
              const status = STATUS_CONFIG[project.status] || STATUS_CONFIG.draft;
              return (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                >
                  <Card className="group hover:shadow-md hover:border-primary/20 transition-all duration-200">
                    <CardContent className="pt-4 pb-3 px-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-semibold truncate">{project.name}</h3>
                          {project.theme && (
                            <p className="text-[11px] text-muted-foreground truncate mt-0.5">{project.theme}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${status.color}`}>
                            {status.label}
                          </Badge>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100">
                                <MoreVertical className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <DropdownMenuItem onClick={() => navigate("/studio/record")} className="text-xs">
                                <Play className="h-3 w-3 mr-2" />
                                Enregistrer
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEdit(project)} className="text-xs">
                                <Pencil className="h-3 w-3 mr-2" />
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleToggleFavorite(project)} className="text-xs">
                                <Star className={`h-3 w-3 mr-2 ${project.is_favorite ? "fill-amber-400 text-amber-400" : ""}`} />
                                {project.is_favorite ? "Retirer favori" : "Ajouter favori"}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleDelete(project)} className="text-xs text-destructive">
                                <Trash2 className="h-3 w-3 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>

                      {project.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{project.description}</p>
                      )}

                      {project.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {project.tags.slice(0, 4).map((tag) => (
                            <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <FileVideo className="h-3 w-3" />
                          {project.recordings_count}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDuration(project.total_duration_seconds)}
                        </span>
                        <span className="flex items-center gap-1">
                          <HardDrive className="h-3 w-3" />
                          {formatSize(project.total_size_bytes)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/30">
                        <span className="text-[10px] text-muted-foreground">
                          {formatDate(project.updated_at)}
                        </span>
                        {project.is_favorite && (
                          <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FolderKanban className="h-12 w-12 text-muted-foreground/30" />
              <h3 className="font-semibold mt-4">
                {search || statusFilter !== "all" || showFavoritesOnly
                  ? "Aucun projet trouvé"
                  : "Aucun projet"}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {search || statusFilter !== "all" || showFavoritesOnly
                  ? "Essayez de modifier vos filtres"
                  : "Créez votre premier projet pour organiser vos enregistrements"}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showNewDialog || !!editProject} onOpenChange={(open) => {
          if (!open) { setShowNewDialog(false); setEditProject(null); }
        }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FolderKanban className="h-5 w-5 text-primary" />
                {editProject ? "Modifier le projet" : "Nouveau projet"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Nom *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Formation Kubernetes HA"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Décrivez le contenu..."
                  rows={3}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Thème</label>
                <Input
                  value={formData.theme}
                  onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                  placeholder="DevOps, Kubernetes, Docker..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => { setShowNewDialog(false); setEditProject(null); }}>
                Annuler
              </Button>
              <Button
                size="sm"
                disabled={saving || !formData.name.trim()}
                onClick={editProject ? handleUpdate : handleCreate}
                style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)", border: "none" }}
              >
                {saving ? "Enregistrement..." : editProject ? "Modifier" : "Créer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </RecordingStudioLayout>
  );
}
